import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  DocumentTextIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  MagnifyingGlassIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://ia.discador.net';

// const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

// Estilos
const labelStyle = "block text-sm font-medium text-foreground mb-1";
const inputStyle = "w-full px-3 py-2 rounded-md border border-input bg-background text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";
const textareaStyle = "w-full px-3 py-2 rounded-md border border-input bg-background text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 font-mono";
const selectStyle = "w-full px-3 py-2 rounded-md border border-input bg-background text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

// Modal
const Modal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur">
      <Card className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="absolute right-3 top-3"
        >
          <XMarkIcon className="h-5 w-5" />
        </Button>
        <CardContent className="pt-6">{children}</CardContent>
      </Card>
    </div>
  );
};

// Templates pré-configurados de Estruturas de Saída
const STRUCTURED_OUTPUT_TEMPLATES = [
  {
    id: 'call-summary',
    name: 'Resumo da chamada',
    description: 'Gera um resumo conciso que captura os pontos principais e os resultados da conversa.',
    icon: '📄',
    type: 'string',
    schema: {
      type: 'string',
      description: 'Você é um especialista em fazer anotações. Resuma a chamada em 2 a 3 frases, focando nos pontos principais e nos resultados. Seja conciso e registre as informações mais importantes.'
    },
    purpose: 'Gera um resumo conciso que captura os pontos principais e os resultados da conversa.'
  },
  {
    id: 'success-evaluation-binary',
    name: 'Avaliação de sucesso - Aprovado/Reprovado',
    description: 'Avaliação binária simples do sucesso da chamada',
    icon: '✅',
    type: 'boolean',
    schema: {
      type: 'boolean',
      description: 'Você é um especialista em avaliação de chamadas. Analise a transcrição da chamada e determine se ela foi bem-sucedida com base nos objetivos inferidos do prompt do sistema. Retorne true se a chamada atingiu seus objetivos com sucesso, false se não atingiu.'
    },
    purpose: 'Avaliação binária de sucesso determinando se a chamada atingiu seus objetivos'
  },
  {
    id: 'success-evaluation-numeric',
    name: 'Avaliação de Sucesso - Escala Numérica',
    description: 'Avalie o sucesso das chamadas em uma escala de 1 a 10.',
    icon: '📊',
    type: 'number',
    schema: {
      type: 'number',
      description: 'Você é um especialista em avaliação de chamadas. Avalie o sucesso desta chamada em uma escala de 1 a 10.\n1 = Falha completa, nenhum objetivo atingido\n2-3 = Desempenho ruim, muito poucos objetivos atingidos\n4-5 = Abaixo da média, alguns objetivos parcialmente atingidos\n6-7 = Desempenho médio, a maioria dos objetivos adequadamente atingidos\n8-9 = Bom desempenho, objetivos bem atingidos\n10 = Execução perfeita, todos os objetivos superados',
      minimum: 1,
      maximum: 10
    },
    purpose: 'Avaliação numérica do sucesso da chamada em uma escala de 1 a 10'
  },
  {
    id: 'success-evaluation-descriptive',
    name: 'Avaliação de Sucesso - Descritiva',
    description: 'Avaliação qualitativa do desempenho das chamadas',
    icon: '💬',
    type: 'string',
    schema: {
      type: 'string',
      description: 'Você é um especialista em avaliação de chamadas. Avalie a qualidade geral e o sucesso desta chamada.\nAvalie como:\n- Excelente: Todos os objetivos atingidos perfeitamente com execução excepcional\n- Bom: A maioria dos objetivos atingidos bem com desempenho sólido\n- Razoável: Alguns objetivos atingidos com desempenho adequado\n- Ruim: Poucos ou nenhum objetivo atingido com desempenho ruim',
      enum: ['Excelente', 'Bom', 'Razoável', 'Ruim']
    },
    purpose: 'Avaliação qualitativa categorizando o desempenho da chamada'
  },
  {
    id: 'appointment-scheduled',
    name: 'Consulta Agendada',
    description: 'Rastreia agendamentos de consultas bem-sucedidos de chamadas de clientes.',
    icon: '📅',
    type: 'boolean',
    schema: {
      type: 'boolean',
      description: 'Marque como true quando o cliente confirmar o agendamento e a consulta estiver agendada'
    },
    purpose: 'Rastreia agendamentos de consultas bem-sucedidos de chamadas de clientes'
  },
  {
    id: 'customer-sentiment',
    name: 'Sentimento do Cliente',
    description: 'Analisa o tom emocional e o sentimento das interações do cliente',
    icon: '❤️',
    type: 'string',
    schema: {
      type: 'string',
      description: 'Avalie o estado emocional do cliente durante toda a interação',
      enum: ['positivo', 'neutro', 'negativo', 'misto']
    },
    purpose: 'Analisa o tom emocional e o sentimento das interações do cliente'
  },
  {
    id: 'appointment-canceled',
    name: 'Consulta Cancelada',
    description: 'Monitora cancelamentos de consultas durante as chamadas.',
    icon: '❌',
    type: 'boolean',
    schema: {
      type: 'boolean',
      description: 'Marque como true quando o cliente cancelar uma consulta existente'
    },
    purpose: 'Monitora cancelamentos de consultas durante as chamadas'
  },
  {
    id: 'appointment-rescheduled',
    name: 'Consulta Remarcada',
    description: 'Captura solicitações de remarcação de consultas.',
    icon: '🔄',
    type: 'boolean',
    schema: {
      type: 'boolean',
      description: 'Marque como true quando o cliente alterar a consulta para uma nova data/hora'
    },
    purpose: 'Captura solicitações de remarcação de consultas'
  },
  {
    id: 'reservation-status',
    name: 'Status da Reserva',
    description: 'Categoriza o resultado final das interações de reserva.',
    icon: '📋',
    type: 'string',
    schema: {
      type: 'string',
      enum: ['reservado', 'pendente', 'cancelado', 'remarcado', 'não_compareceu'],
      description: 'Rastreie o resultado da interação de reserva'
    },
    purpose: 'Categoriza o resultado final das interações de reserva'
  }
];

// Lista de modelos disponíveis
const AVAILABLE_MODELS = [
  'gpt-5.2',
  'gpt-5.2-chat-latest',
  'gpt-5.1',
  'gpt-5.1-chat-latest',
  'gpt-5',
  'gpt-5-chat-latest',
  'gpt-5-mini',
  'gpt-5-nano',
  'gpt-4.1-2025-04-14',
  'gpt-4.1-mini-2025-04-14',
  'gpt-4.1-nano-2025-04-14',
  'gpt-4.1',
  'gpt-4.1-mini',
  'gpt-4.1-nano',
  'chatgpt-4o-latest',
  'o3',
  'o3-mini',
  'o4-mini',
  'o1-mini',
  'o1-mini-2024-09-12',
  'gpt-4o-mini-2024-07-18',
  'gpt-4o-mini',
  'gpt-4o',
  'gpt-4o-2024-05-13',
  'gpt-4o-2024-08-06',
  'gpt-4o-2024-11-20',
  'gpt-4-turbo',
  'gpt-4-turbo-2024-04-09',
  'gpt-4-turbo-preview',
  'gpt-4-0125-preview',
  'gpt-4-1106-preview',
  'gpt-4',
  'gpt-4-0613',
  'gpt-3.5-turbo',
  'gpt-3.5-turbo-0125',
  'gpt-3.5-turbo-1106',
  'gpt-3.5-turbo-16k',
  'gpt-3.5-turbo-0613',
  'gpt-4.1-2025-04-14:westus',
  'gpt-4.1-2025-04-14:eastus2',
  'gpt-4.1-2025-04-14:eastus',
  'gpt-4.1-2025-04-14:westus3',
  'gpt-4.1-2025-04-14:northcentralus',
  'gpt-4.1-2025-04-14:southcentralus',
  'gpt-4.1-2025-04-14:westeurope',
  'gpt-4.1-2025-04-14:germanywestcentral',
  'gpt-4.1-2025-04-14:polandcentral',
  'gpt-4.1-2025-04-14:spaincentral',
  'gpt-4.1-mini-2025-04-14:westus',
  'gpt-4.1-mini-2025-04-14:eastus2',
  'gpt-4.1-mini-2025-04-14:eastus',
  'gpt-4.1-mini-2025-04-14:westus3',
  'gpt-4.1-mini-2025-04-14:northcentralus',
  'gpt-4.1-mini-2025-04-14:southcentralus',
  'gpt-4.1-mini-2025-04-14:westeurope',
  'gpt-4.1-mini-2025-04-14:germanywestcentral',
  'gpt-4.1-mini-2025-04-14:polandcentral',
  'gpt-4.1-mini-2025-04-14:spaincentral',
  'gpt-4.1-nano-2025-04-14:westus',
  'gpt-4.1-nano-2025-04-14:eastus2',
  'gpt-4.1-nano-2025-04-14:westus3',
  'gpt-4.1-nano-2025-04-14:northcentralus',
  'gpt-4.1-nano-2025-04-14:southcentralus',
  'gpt-4o-2024-11-20:swedencentral',
  'gpt-4o-2024-11-20:westus',
  'gpt-4o-2024-11-20:eastus2',
  'gpt-4o-2024-11-20:eastus',
  'gpt-4o-2024-11-20:westus3',
  'gpt-4o-2024-11-20:southcentralus',
  'gpt-4o-2024-11-20:westeurope',
  'gpt-4o-2024-11-20:germanywestcentral',
  'gpt-4o-2024-11-20:polandcentral',
  'gpt-4o-2024-11-20:spaincentral',
  'gpt-4o-2024-08-06:westus',
  'gpt-4o-2024-08-06:westus3',
  'gpt-4o-2024-08-06:eastus',
  'gpt-4o-2024-08-06:eastus2',
  'gpt-4o-2024-08-06:northcentralus',
  'gpt-4o-2024-08-06:southcentralus',
  'gpt-4o-mini-2024-07-18:westus',
  'gpt-4o-mini-2024-07-18:westus3',
  'gpt-4o-mini-2024-07-18:eastus',
  'gpt-4o-mini-2024-07-18:eastus2',
  'gpt-4o-mini-2024-07-18:northcentralus',
  'gpt-4o-mini-2024-07-18:southcentralus',
  'gpt-4o-2024-05-13:eastus2',
  'gpt-4o-2024-05-13:eastus',
  'gpt-4o-2024-05-13:northcentralus',
  'gpt-4o-2024-05-13:southcentralus',
  'gpt-4o-2024-05-13:westus3',
  'gpt-4o-2024-05-13:westus',
  'gpt-4-turbo-2024-04-09:eastus2',
  'gpt-4-0125-preview:eastus',
  'gpt-4-0125-preview:northcentralus',
  'gpt-4-0125-preview:southcentralus',
  'gpt-4-1106-preview:australia',
  'gpt-4-1106-preview:canadaeast',
  'gpt-4-1106-preview:france',
  'gpt-4-1106-preview:india',
  'gpt-4-1106-preview:norway',
  'gpt-4-1106-preview:swedencentral',
  'gpt-4-1106-preview:uk',
  'gpt-4-1106-preview:westus',
  'gpt-4-1106-preview:westus3',
  'gpt-4-0613:canadaeast',
  'gpt-3.5-turbo-0125:canadaeast',
  'gpt-3.5-turbo-0125:northcentralus',
  'gpt-3.5-turbo-0125:southcentralus',
  'gpt-3.5-turbo-1106:canadaeast',
  'gpt-3.5-turbo-1106:westus'
];

// Helper para parsear schema existente em campos visuais
const parseSchemaToVisual = (schema) => {
  if (!schema || typeof schema === 'string') {
    try {
      schema = schema ? JSON.parse(schema) : {};
    } catch {
      schema = {};
    }
  }

  const visualData = {
    type: schema.type || 'string',
    description: schema.description || '',
    enum: schema.enum || [],
    minimum: schema.minimum !== undefined ? schema.minimum.toString() : '',
    maximum: schema.maximum !== undefined ? schema.maximum.toString() : '',
    items: schema.items ? JSON.stringify(schema.items, null, 2) : '',
    properties: schema.properties ? JSON.stringify(schema.properties, null, 2) : '',
    required: schema.required || []
  };

  return visualData;
};

// Helper para gerar JSON Schema a partir dos campos visuais
const generateSchemaFromVisual = (visualData) => {
  const schema = {
    type: visualData.type
  };

  // description é permitido no schema segundo a documentação do provedor
  // Ele ajuda o modelo a entender o que precisa gerar como saída
  if (visualData.description && visualData.description.trim()) {
    schema.description = visualData.description.trim();
  }

  // Campos específicos por tipo
  if (visualData.type === 'string') {
    if (visualData.enum && visualData.enum.length > 0) {
      schema.enum = visualData.enum.filter(v => v.trim() !== '');
    }
  }

  if (visualData.type === 'number' || visualData.type === 'integer') {
    if (visualData.minimum && visualData.minimum.trim() !== '') {
      schema.minimum = visualData.type === 'integer' 
        ? parseInt(visualData.minimum) 
        : parseFloat(visualData.minimum);
    }
    if (visualData.maximum && visualData.maximum.trim() !== '') {
      schema.maximum = visualData.type === 'integer'
        ? parseInt(visualData.maximum)
        : parseFloat(visualData.maximum);
    }
  }

  if (visualData.type === 'array') {
    if (visualData.items && visualData.items.trim() !== '') {
      try {
        schema.items = JSON.parse(visualData.items);
      } catch (e) {
        // Se não conseguir parsear, usar objeto vazio
        schema.items = {};
      }
    }
  }

  if (visualData.type === 'object') {
    if (visualData.properties && visualData.properties.trim() !== '') {
      try {
        schema.properties = JSON.parse(visualData.properties);
      } catch (e) {
        schema.properties = {};
      }
    }
    if (visualData.required && visualData.required.length > 0) {
      schema.required = visualData.required.filter(r => r.trim() !== '');
    }
  }

  return schema;
};

// Componente de seleção de assistentes com busca
const AssistantSelector = ({ selectedAssistantIds, onSelectionChange }) => {
  const [assistants, setAssistants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const fetchAssistants = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const userData = JSON.parse(localStorage.getItem('userData') || '{}');
        const clienteId = userData.clienteId || userData._id || userData.id;
        const tokenClient = userData.token || userData.tokenClient;

        const url = clienteId
          ? `${API_BASE_URL}/api/assistentes/master?clienteId=${clienteId}&tokenClient=${encodeURIComponent(tokenClient || '')}`
          : `${API_BASE_URL}/api/assistentes/master`;

        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        // Filtrar apenas assistentes que têm vapiId
        const assistantsWithVapiId = (response.data || []).filter(a => a.vapiId || a.id);
        setAssistants(assistantsWithVapiId);
      } catch (error) {
        console.error('Erro ao buscar assistentes:', error);
        toast.error('Erro ao carregar assistentes');
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchAssistants();
    }
  }, [isOpen]);

  const filteredAssistants = assistants.filter(assistant => {
    const name = (assistant.nome || assistant.name || '').toLowerCase();
    const id = (assistant.vapiId || assistant.id || '').toString().toLowerCase();
    const query = searchQuery.toLowerCase();
    return name.includes(query) || id.includes(query);
  });

  const toggleAssistant = (assistant) => {
    const vapiId = assistant.vapiId || assistant.id;
    const currentIds = Array.isArray(selectedAssistantIds) ? selectedAssistantIds : [];
    
    if (currentIds.includes(vapiId)) {
      onSelectionChange(currentIds.filter(id => id !== vapiId));
    } else {
      onSelectionChange([...currentIds, vapiId]);
    }
  };

  const removeAssistant = (vapiId) => {
    const currentIds = Array.isArray(selectedAssistantIds) ? selectedAssistantIds : [];
    onSelectionChange(currentIds.filter(id => id !== vapiId));
  };

  const selectedAssistants = assistants.filter(a => {
    const vapiId = a.vapiId || a.id;
    return Array.isArray(selectedAssistantIds) && selectedAssistantIds.includes(vapiId);
  });

  return (
    <div className="space-y-2">
      <Label className={labelStyle}>
        Assistentes (opcional)
      </Label>
      
      {/* Assistentes selecionados */}
      {selectedAssistants.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {selectedAssistants.map(assistant => {
            const vapiId = assistant.vapiId || assistant.id;
            return (
              <Badge
                key={vapiId}
                variant="secondary"
                className="flex items-center gap-1 pr-1"
              >
                <span className="text-xs">
                  {assistant.nome || assistant.name || 'Sem nome'} ({vapiId?.toString().substring(0, 8)}...)
                </span>
                <button
                  type="button"
                  onClick={() => removeAssistant(vapiId)}
                  className="ml-1 hover:bg-destructive/20 rounded-full p-0.5"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </Badge>
            );
          })}
        </div>
      )}

      {/* Dropdown de seleção */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`${inputStyle} flex items-center justify-between cursor-pointer`}
        >
          <span className="text-muted-foreground">
            {loading ? 'Carregando...' : 'Selecione assistentes'}
          </span>
          <ChevronDownIcon className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-background border border-border rounded-md shadow-lg max-h-60 overflow-hidden">
            {/* Barra de busca */}
            <div className="p-2 border-b border-border">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar assistentes..."
                  className="w-full pl-8 pr-2 py-1.5 text-sm border border-input rounded-md bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>
            </div>

            {/* Lista de assistentes */}
            <div className="max-h-48 overflow-y-auto">
              {loading ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  Carregando...
                </div>
              ) : filteredAssistants.length === 0 ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  {searchQuery ? 'Nenhum assistente encontrado' : 'Nenhum assistente disponível'}
                </div>
              ) : (
                filteredAssistants.map(assistant => {
                  const vapiId = assistant.vapiId || assistant.id;
                  const isSelected = Array.isArray(selectedAssistantIds) && selectedAssistantIds.includes(vapiId);
                  const displayId = vapiId?.toString().substring(0, 12) || 'N/A';
                  
                  return (
                    <button
                      key={vapiId}
                      type="button"
                      onClick={() => toggleAssistant(assistant)}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-muted/50 flex items-center justify-between ${
                        isSelected ? 'bg-muted' : ''
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">
                          {assistant.nome || assistant.name || 'Sem nome'}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          ID: {displayId}
                        </div>
                      </div>
                      {isSelected && (
                        <CheckIcon className="h-4 w-4 text-primary flex-shrink-0 ml-2" />
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* Overlay para fechar ao clicar fora */}
      {isOpen && (
        <div
          className="fixed inset-0 z-30"
          onClick={() => setIsOpen(false)}
          style={{ backgroundColor: 'transparent' }}
        />
      )}
    </div>
  );
};

// Form
const StructuredOutputForm = ({ structuredOutput, onSave, onCancel, isSubmitting }) => {
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [useVisualBuilder, setUseVisualBuilder] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    schema: JSON.stringify({
      type: 'string',
      description: ''
    }, null, 2),
    modelProvider: 'default',
    modelName: '',
    temperature: '',
    maxTokens: '',
    assistantIds: [],
    compliancePlan: null
  });

  // Estado para o builder visual do schema
  const [schemaVisual, setSchemaVisual] = useState({
    type: 'string',
    description: '',
    enum: [],
    minimum: '',
    maximum: '',
    items: '',
    properties: '',
    required: []
  });

  useEffect(() => {
    if (structuredOutput) {
      const model = structuredOutput.model;
      // Se vier de template, usar purpose como description
      const description = structuredOutput._isTemplate 
        ? structuredOutput.description 
        : structuredOutput.description || '';
      
      // Parse schema se for string (vindo de template)
      let schema = structuredOutput.schema || {};
      if (typeof schema === 'string') {
        try {
          schema = JSON.parse(schema);
        } catch (e) {
          console.error('Erro ao fazer parse do schema:', e);
          schema = {};
        }
      }
      const parsedVisual = parseSchemaToVisual(schema);
      
      // Gerar schema a partir do visual para garantir consistência
      const generatedSchema = generateSchemaFromVisual(parsedVisual);
      const schemaString = JSON.stringify(generatedSchema, null, 2);
      
      setFormData({
        name: structuredOutput.name || '',
        description: description,
        schema: schemaString,
        modelProvider: model?.provider === 'openai' ? 'openai' : 'default',
        modelName: model?.model || '',
        temperature: model?.temperature?.toString() || '',
        maxTokens: model?.maxTokens?.toString() || '',
        assistantIds: structuredOutput.assistantIds || [],
        compliancePlan: structuredOutput.compliancePlan || null
      });
      
      setSchemaVisual(parsedVisual);
      
      // Abrir seção avançada se houver configurações de modelo
      if (model && model.provider === 'openai') {
        setIsAdvancedOpen(true);
      }
    } else {
      // Resetar formulário quando não houver structuredOutput
      const defaultVisual = {
        type: 'string',
        description: '',
        enum: [],
        minimum: '',
        maximum: '',
        items: '',
        properties: '',
        required: []
      };
      
      setFormData({
        name: '',
        description: '',
        schema: JSON.stringify({ type: 'string', description: '' }, null, 2),
        modelProvider: 'default',
        modelName: '',
        temperature: '',
        maxTokens: '',
        assistantIds: [],
        compliancePlan: null
      });
      
      setSchemaVisual(defaultVisual);
      setIsAdvancedOpen(false);
    }
  }, [structuredOutput]);

  // Atualizar schema JSON quando mudar para visual builder
  useEffect(() => {
    if (useVisualBuilder) {
      const generatedSchema = generateSchemaFromVisual(schemaVisual);
      const schemaString = JSON.stringify(generatedSchema, null, 2);
      setFormData(prev => {
        // Só atualizar se o schema gerado for diferente do atual
        if (prev.schema !== schemaString) {
          return {
            ...prev,
            schema: schemaString
          };
        }
        return prev;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useVisualBuilder, schemaVisual]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      
      // Se mudar para default, limpar campos de modelo
      if (name === 'modelProvider' && value === 'default') {
        newData.modelName = '';
        newData.temperature = '';
        newData.maxTokens = '';
      }
      
      return newData;
    });
  };

  const handleSchemaChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      schema: value
    }));
    
    // Tentar atualizar campos visuais se possível
    if (useVisualBuilder) {
      try {
        const parsed = JSON.parse(value);
        const visual = parseSchemaToVisual(parsed);
        setSchemaVisual(visual);
      } catch {
        // Ignorar erros de parsing
      }
    }
  };

  const handleSchemaVisualChange = (field, value) => {
    setSchemaVisual(prev => {
      const updated = {
        ...prev,
        [field]: value
      };
      
      // Limpar campos irrelevantes quando mudar o tipo
      if (field === 'type') {
        if (value !== 'string') {
          updated.enum = [];
        }
        if (value !== 'number' && value !== 'integer') {
          updated.minimum = '';
          updated.maximum = '';
        }
        if (value !== 'array') {
          updated.items = '';
        }
        if (value !== 'object') {
          updated.properties = '';
          updated.required = [];
        }
      }
      
      // Atualizar schema JSON imediatamente se estiver usando builder visual
      if (useVisualBuilder) {
        const generatedSchema = generateSchemaFromVisual(updated);
        const schemaString = JSON.stringify(generatedSchema, null, 2);
        setFormData(formPrev => ({
          ...formPrev,
          schema: schemaString
        }));
      }
      
      return updated;
    });
  };

  const updateSchemaFromVisual = (visualData) => {
    if (useVisualBuilder) {
      const generatedSchema = generateSchemaFromVisual(visualData);
      const schemaString = JSON.stringify(generatedSchema, null, 2);
      setFormData(prev => ({
        ...prev,
        schema: schemaString
      }));
    }
  };

  const handleAddEnumValue = () => {
    setSchemaVisual(prev => {
      const updated = {
        ...prev,
        enum: [...prev.enum, '']
      };
      updateSchemaFromVisual(updated);
      return updated;
    });
  };

  const handleEnumValueChange = (index, value) => {
    setSchemaVisual(prev => {
      const updated = {
        ...prev,
        enum: prev.enum.map((v, i) => i === index ? value : v)
      };
      updateSchemaFromVisual(updated);
      return updated;
    });
  };

  const handleRemoveEnumValue = (index) => {
    setSchemaVisual(prev => {
      const updated = {
        ...prev,
        enum: prev.enum.filter((_, i) => i !== index)
      };
      updateSchemaFromVisual(updated);
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Validar schema JSON
      const schemaObj = JSON.parse(formData.schema);
      if (!schemaObj.type) {
        toast.error('O schema deve ter um campo "type"');
        return;
      }

      // Obter clienteId e tokenClient do localStorage
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const clienteId = userData.clienteId || userData._id || userData.id;
      // Tentar buscar tokenClient de diferentes lugares
      const tokenClient = userData.tokenClient || userData.token || localStorage.getItem('tokenClient');

      const payload = {
        name: formData.name,
        description: formData.description,
        schema: schemaObj
      };

      // Adicionar clienteId e tokenClient apenas se existirem
      if (clienteId) {
        payload.clienteId = clienteId;
      }
      if (tokenClient) {
        payload.tokenClient = tokenClient;
      }

      // Adicionar configurações de modelo se não for padrão
      if (formData.modelProvider === 'openai') {
        if (!formData.modelName || formData.modelName.trim() === '') {
          toast.error('Selecione um modelo quando usar OpenAI como provider');
          return;
        }
        
        payload.model = {
          provider: 'openai',
          model: formData.modelName
        };
        
        // Adicionar temperatura se preenchida
        if (formData.temperature && formData.temperature.trim() !== '') {
          const temp = parseFloat(formData.temperature);
          if (isNaN(temp) || temp < 0 || temp > 2) {
            toast.error('A temperatura deve ser um número entre 0 e 2');
            return;
          }
          payload.model.temperature = temp;
        }
        
        // Adicionar maxTokens se preenchido
        if (formData.maxTokens && formData.maxTokens.trim() !== '') {
          const tokens = parseInt(formData.maxTokens);
          if (isNaN(tokens) || tokens < 50 || tokens > 10000) {
            toast.error('Max Tokens deve ser um número entre 50 e 10.000');
            return;
          }
          payload.model.maxTokens = tokens;
        }
      }

      // Usar vapiId dos assistentes selecionados
      if (formData.assistantIds && formData.assistantIds.length > 0) {
        payload.assistantIds = Array.isArray(formData.assistantIds)
          ? formData.assistantIds
          : typeof formData.assistantIds === 'string'
          ? formData.assistantIds.split(',').map(id => id.trim()).filter(Boolean)
          : [];
      }

      if (formData.compliancePlan) {
        try {
          payload.compliancePlan = typeof formData.compliancePlan === 'string'
            ? JSON.parse(formData.compliancePlan)
            : formData.compliancePlan;
        } catch (e) {
          toast.error('Compliance Plan inválido. Deve ser um JSON válido.');
          return;
        }
      }

      // Log para debug
      console.log('Payload sendo enviado:', JSON.stringify(payload, null, 2));
      console.log('ClienteId:', clienteId);
      console.log('TokenClient:', tokenClient ? 'Presente' : 'Ausente');

      await onSave(payload);
    } catch (error) {
      if (error.message.includes('JSON')) {
        toast.error('Schema JSON inválido. Verifique a sintaxe.');
      } else {
        toast.error('Erro ao validar formulário: ' + error.message);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <h2 className="text-2xl font-semibold">
        {structuredOutput ? 'Editar' : 'Criar'} Estrutura de Saída
      </h2>

      <div>
        <Label htmlFor="name" className={labelStyle}>
          Nome <span className="text-red-500">*</span>
        </Label>
        <input
          id="name"
          name="name"
          type="text"
          value={formData.name}
          onChange={handleChange}
          className={inputStyle}
          required
          placeholder="Ex: Informações do Cliente"
        />
      </div>

      <div>
        <Label htmlFor="description" className={labelStyle}>
          Qual é o propósito desta saída estruturada?
        </Label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          className={textareaStyle}
          rows={3}
          placeholder="Descreva o propósito e o que será extraído por esta estrutura de saída"
        />
      </div>

      {/* Seção SCHEMA */}
      <div className="border-t border-border pt-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">SCHEMA</h3>
          <button
            type="button"
            onClick={() => setUseVisualBuilder(!useVisualBuilder)}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            {useVisualBuilder ? 'Editar JSON manualmente' : 'Usar construtor visual'}
          </button>
        </div>

        {useVisualBuilder ? (
          <div className="space-y-4">
            {/* Type of Output */}
            <div>
              <Label htmlFor="schemaType" className={labelStyle}>
                Tipo de Saída <span className="text-red-500">*</span>
              </Label>
              <select
                id="schemaType"
                value={schemaVisual.type}
                onChange={(e) => handleSchemaVisualChange('type', e.target.value)}
                className={selectStyle}
                required
              >
                <option value="string">String</option>
                <option value="boolean">Boolean</option>
                <option value="number">Number</option>
                <option value="integer">Integer</option>
                <option value="array">Array</option>
                <option value="object">Object</option>
              </select>
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="schemaDescription" className={labelStyle}>
                Descrição
              </Label>
              <textarea
                id="schemaDescription"
                value={schemaVisual.description}
                onChange={(e) => handleSchemaVisualChange('description', e.target.value)}
                className={textareaStyle}
                rows={4}
                placeholder="Descreva o que deve ser extraído..."
              />
            </div>

            {/* Allowed Values (enum) - apenas para String */}
            {schemaVisual.type === 'string' && (
              <div>
                <Label className={labelStyle}>
                  Valores Permitidos
                </Label>
                <p className="mb-2 text-xs text-muted-foreground">
                  Restrinja o valor da string a um conjunto específico de valores permitidos (enums).
                </p>
                <div className="space-y-2">
                  {schemaVisual.enum.map((value, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={value}
                        onChange={(e) => handleEnumValueChange(index, e.target.value)}
                        className={inputStyle}
                        placeholder="Valor permitido"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleRemoveEnumValue(index)}
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddEnumValue}
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Adicionar Valor
                  </Button>
                </div>
              </div>
            )}

            {/* Min/Max - para Number e Integer */}
            {(schemaVisual.type === 'number' || schemaVisual.type === 'integer') && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="schemaMinimum" className={labelStyle}>
                    Mínimo
                  </Label>
                  <input
                    id="schemaMinimum"
                    type="number"
                    value={schemaVisual.minimum}
                    onChange={(e) => handleSchemaVisualChange('minimum', e.target.value)}
                    className={inputStyle}
                    placeholder="Mínimo"
                  />
                </div>
                <div>
                  <Label htmlFor="schemaMaximum" className={labelStyle}>
                    Máximo
                  </Label>
                  <input
                    id="schemaMaximum"
                    type="number"
                    value={schemaVisual.maximum}
                    onChange={(e) => handleSchemaVisualChange('maximum', e.target.value)}
                    className={inputStyle}
                    placeholder="Máximo"
                  />
                </div>
              </div>
            )}

            {/* Items - para Array */}
            {schemaVisual.type === 'array' && (
              <div>
                <Label htmlFor="schemaItems" className={labelStyle}>
                  Schema dos Itens
                </Label>
                <p className="mb-2 text-xs text-muted-foreground">
                  Defina o schema dos itens do array (JSON Schema).
                </p>
                <textarea
                  id="schemaItems"
                  value={schemaVisual.items}
                  onChange={(e) => handleSchemaVisualChange('items', e.target.value)}
                  className={textareaStyle}
                  rows={6}
                  placeholder='{"type": "string"}'
                />
              </div>
            )}

            {/* Properties e Required - para Object */}
            {schemaVisual.type === 'object' && (
              <>
                <div>
                  <Label htmlFor="schemaProperties" className={labelStyle}>
                    Propriedades
                  </Label>
                  <p className="mb-2 text-xs text-muted-foreground">
                    Defina as propriedades do objeto (JSON Schema).
                  </p>
                  <textarea
                    id="schemaProperties"
                    value={schemaVisual.properties}
                    onChange={(e) => handleSchemaVisualChange('properties', e.target.value)}
                    className={textareaStyle}
                    rows={8}
                    placeholder='{"name": {"type": "string"}, "age": {"type": "number"}}'
                  />
                </div>
                <div>
                  <Label htmlFor="schemaRequired" className={labelStyle}>
                    Campos Obrigatórios
                  </Label>
                  <p className="mb-2 text-xs text-muted-foreground">
                    Lista de campos obrigatórios (separados por vírgula).
                  </p>
                  <input
                    id="schemaRequired"
                    type="text"
                    value={Array.isArray(schemaVisual.required) ? schemaVisual.required.join(', ') : schemaVisual.required}
                    onChange={(e) => {
                      const required = e.target.value.split(',').map(r => r.trim()).filter(Boolean);
                      handleSchemaVisualChange('required', required);
                    }}
                    className={inputStyle}
                    placeholder="campo1, campo2"
                  />
                </div>
              </>
            )}

            {/* Preview do Schema JSON gerado */}
            <div className="rounded-lg border border-border bg-muted/30 p-4">
              <Label className={labelStyle}>Preview do Schema JSON:</Label>
              <pre className="text-xs text-muted-foreground overflow-x-auto">
                {formData.schema}
              </pre>
            </div>
          </div>
        ) : (
          <div>
            <Label htmlFor="schema" className={labelStyle}>
              Schema JSON <span className="text-red-500">*</span>
            </Label>
            <textarea
              id="schema"
              name="schema"
              value={formData.schema}
              onChange={handleSchemaChange}
              className={textareaStyle}
              rows={15}
              required
              placeholder='{"type": "string", "description": "..."}'
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Defina a estrutura JSON Schema que será extraída. Suporta objetos, arrays, tipos primitivos, enums, etc.
            </p>
          </div>
        )}
      </div>

      <AssistantSelector
        selectedAssistantIds={formData.assistantIds}
        onSelectionChange={(ids) => setFormData(prev => ({ ...prev, assistantIds: ids }))}
      />

      {/* Seção Avançada */}
      <div className="border-t border-border pt-4">
        <button
          type="button"
          onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
          className="flex w-full items-center justify-between py-2 text-left"
        >
          <span className="text-lg font-semibold">AVANÇADO</span>
          {isAdvancedOpen ? (
            <ChevronUpIcon className="h-5 w-5" />
          ) : (
            <ChevronDownIcon className="h-5 w-5" />
          )}
        </button>
        <p className="mb-4 text-sm text-muted-foreground">
          Configurações avançadas do LLM
        </p>

        {isAdvancedOpen && (
          <div className="space-y-4 rounded-lg border border-border bg-muted/30 p-4">
            <div>
              <Label htmlFor="modelProvider" className={labelStyle}>
                Fornecedor de modelos
              </Label>
              <p className="mb-2 text-xs text-muted-foreground">
                Opcional. Selecione um provedor específico para substituir o extrator GPT-4.1 padrão.
              </p>
              <select
                id="modelProvider"
                name="modelProvider"
                value={formData.modelProvider}
                onChange={handleChange}
                className={selectStyle}
              >
                <option value="default">Usar padrão (GPT-4.1)</option>
                <option value="openai">OpenAI</option>
              </select>
            </div>

            {formData.modelProvider === 'openai' && (
              <>
                <div>
                  <Label htmlFor="modelName" className={labelStyle}>
                    Modelo
                  </Label>
                  <select
                    id="modelName"
                    name="modelName"
                    value={formData.modelName}
                    onChange={handleChange}
                    className={selectStyle}
                    required={formData.modelProvider === 'openai'}
                  >
                    <option value="">Selecione um modelo</option>
                    {AVAILABLE_MODELS.map((model) => (
                      <option key={model} value={model}>
                        {model}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label htmlFor="temperature" className={labelStyle}>
                    Temperatura
                  </Label>
                  <p className="mb-2 text-xs text-muted-foreground">
                    Opcional. Intervalo de 0 a 2. Deixe em branco para usar o valor padrão do modelo selecionado.
                  </p>
                  <input
                    id="temperature"
                    name="temperature"
                    type="number"
                    min="0"
                    max="2"
                    step="0.1"
                    value={formData.temperature}
                    onChange={handleChange}
                    className={inputStyle}
                    placeholder="Padrão"
                  />
                </div>

                <div>
                  <Label htmlFor="maxTokens" className={labelStyle}>
                    Fichas Máximas
                  </Label>
                  <p className="mb-2 text-xs text-muted-foreground">
                    Opcional. Intervalo típico de 50 a 10.000. Deixe em branco para herdar o padrão do modelo.
                  </p>
                  <input
                    id="maxTokens"
                    name="maxTokens"
                    type="number"
                    min="50"
                    max="10000"
                    value={formData.maxTokens}
                    onChange={handleChange}
                    className={inputStyle}
                    placeholder="Padrão"
                  />
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <div className="flex justify-end gap-4 pt-4">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Salvando...' : structuredOutput ? 'Atualizar' : 'Criar'}
        </Button>
      </div>
    </form>
  );
};

// Modal de seleção de template
const TemplateSelectionModal = ({ isOpen, onClose, onSelectTemplate, onCreateFromScratch }) => {
  const [searchQuery, setSearchQuery] = useState('');

  if (!isOpen) return null;

  const filteredTemplates = STRUCTURED_OUTPUT_TEMPLATES.filter(template =>
    template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    template.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getTypeColor = (type) => {
    switch (type) {
      case 'string': return 'bg-green-500';
      case 'boolean': return 'bg-blue-500';
      case 'number': return 'bg-orange-500';
      case 'integer': return 'bg-purple-500';
      default: return 'bg-gray-500';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'string': return 'corda';
      case 'boolean': return 'booleano';
      case 'number': return 'número';
      case 'integer': return 'inteiro';
      default: return type;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur">
      <Card className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-2xl font-semibold">Criar saída estruturada</h2>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <XMarkIcon className="h-5 w-5" />
          </Button>
        </div>

        <CardContent className="p-6 space-y-6">
          {/* Criar do zero */}
          <button
            type="button"
            onClick={onCreateFromScratch}
            className="w-full p-4 rounded-lg border-2 border-dashed border-border bg-muted/30 hover:bg-muted/50 transition-colors text-left"
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-green-500 flex items-center justify-center text-white text-xl">
                +
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg mb-1">Criar do zero</h3>
                <p className="text-sm text-muted-foreground">
                  Crie uma saída estruturada totalmente personalizada com seu próprio esquema.
                </p>
              </div>
            </div>
          </button>

          {/* Templates pré-configurados */}
          <div>
            <h3 className="text-lg font-semibold mb-4">
              Ou escolha um dos nossos modelos de saída estruturada pré-configurados.
            </h3>
            
            {/* Barra de pesquisa */}
            <div className="mb-4">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Pesquise modelos por nome ou descrição..."
                className={inputStyle}
              />
            </div>

            {/* Grid de templates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredTemplates.map((template) => (
                <button
                  key={template.id}
                  type="button"
                  onClick={() => onSelectTemplate(template)}
                  className="p-4 rounded-lg border border-border bg-card hover:bg-muted/50 transition-colors text-left"
                >
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">{template.icon}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold truncate">{template.name}</h4>
                        <div className={`w-2 h-2 rounded-full ${getTypeColor(template.type)} flex-shrink-0`} />
                        <span className="text-xs text-muted-foreground capitalize flex-shrink-0">
                          {getTypeLabel(template.type)}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {template.description}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {filteredTemplates.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                Nenhum template encontrado para "{searchQuery}"
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Componente principal
const StructuredOutputs = () => {
  const [structuredOutputs, setStructuredOutputs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingStructuredOutput, setEditingStructuredOutput] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Filtros
  const [nameFilter, setNameFilter] = useState('');
  const [dataInicioFilter, setDataInicioFilter] = useState('');
  const [dataFimFilter, setDataFimFilter] = useState('');
  const [clienteIdFilter, setClienteIdFilter] = useState('');
  const [clientes, setClientes] = useState([]);
  
  // Verificar se é admin
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const isAdmin = userData.role === 'admin';

  const fetchStructuredOutputs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Construir query params
      const params = new URLSearchParams();
      
      // Se não for admin, usar clienteId do usuário logado
      if (!isAdmin) {
        const defaultClienteId = userData.clienteId || userData._id || userData.id;
        if (defaultClienteId) {
          params.append('clienteId', defaultClienteId);
        }
      } else if (clienteIdFilter) {
        // Se for admin e tiver filtro de cliente, usar o filtro
        params.append('clienteId', clienteIdFilter);
      }
      
      // Adicionar filtros
      if (nameFilter) {
        params.append('name', nameFilter);
      }
      if (dataInicioFilter) {
        params.append('dataInicio', dataInicioFilter);
      }
      if (dataFimFilter) {
        params.append('dataFim', dataFimFilter);
      }
      
      const url = `${API_BASE_URL}/api/structured-outputs${params.toString() ? '?' + params.toString() : ''}`;
      
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setStructuredOutputs(response.data);
    } catch (error) {
      console.error('Erro ao buscar estruturas de saída:', error);
      toast.error('Erro ao carregar estruturas de saída');
    } finally {
      setLoading(false);
    }
  };

  const fetchClientes = async () => {
    if (!isAdmin) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/usuarios`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setClientes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao buscar clientes:', error);
    }
  };

  useEffect(() => {
    fetchStructuredOutputs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameFilter, dataInicioFilter, dataFimFilter, clienteIdFilter, isAdmin]);

  useEffect(() => {
    if (isAdmin) {
      fetchClientes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const handleCreate = () => {
    setEditingStructuredOutput(null);
    setIsTemplateModalOpen(true);
  };

  const handleSelectTemplate = (template) => {
    setIsTemplateModalOpen(false);
    setEditingStructuredOutput(null);
    
    // Preencher formulário com dados do template
    const templateData = {
      name: template.name,
      description: template.purpose,
      schema: JSON.stringify(template.schema, null, 2),
      modelProvider: 'default',
      modelName: '',
      temperature: '',
      maxTokens: '',
      assistantIds: [],
      compliancePlan: null
    };
    
    setEditingStructuredOutput({ ...templateData, _isTemplate: true });
    setIsFormModalOpen(true);
  };

  const handleCreateFromScratch = () => {
    setIsTemplateModalOpen(false);
    setEditingStructuredOutput(null);
    setIsFormModalOpen(true);
  };

  const handleEdit = (structuredOutput) => {
    setEditingStructuredOutput(structuredOutput);
    setIsFormModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja deletar esta estrutura de saída?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/api/structured-outputs/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      toast.success('Estrutura de saída deletada com sucesso');
      fetchStructuredOutputs();
    } catch (error) {
      console.error('Erro ao deletar estrutura de saída:', error);
      toast.error('Erro ao deletar estrutura de saída: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDuplicate = async (structuredOutput) => {
    const apiKey = window.prompt(
      'Informe a API Key para criar a cópia no sistema. Deixe a atual ou cole outra:',
      localStorage.getItem('tokenClient') || ''
    );
    if (apiKey === null) return;
    if (!apiKey.trim()) {
      toast.error('API Key é obrigatória para duplicar.');
      return;
    }

    const token = localStorage.getItem('token');
    const defaultClienteId = userData.clienteId || userData._id || userData.id;
    const baseName = structuredOutput.name || 'Estrutura';
    const suffix = ' (copia)';
    const duplicatedName = baseName.length + suffix.length <= 40
      ? `${baseName}${suffix}`
      : `${baseName.substring(0, 40 - suffix.length)}${suffix}`;
    const payload = {
      name: duplicatedName,
      description: structuredOutput.description || '',
      schema: structuredOutput.schema,
      type: structuredOutput.type || 'ai',
      model: structuredOutput.model,
      assistantIds: Array.isArray(structuredOutput.assistantIds) ? structuredOutput.assistantIds : [],
      workflowIds: Array.isArray(structuredOutput.workflowIds) ? structuredOutput.workflowIds : [],
      compliancePlan: structuredOutput.compliancePlan || undefined,
      clienteId: isAdmin ? (clienteIdFilter || structuredOutput.clienteId?._id || structuredOutput.clienteId || defaultClienteId) : defaultClienteId,
      tokenClient: apiKey.trim()
    };

    const toastId = toast.loading('Duplicando estrutura de saída...');
    try {
      await axios.post(`${API_BASE_URL}/api/structured-outputs`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      toast.success('Estrutura de saída duplicada com sucesso!', { id: toastId });
      fetchStructuredOutputs();
    } catch (error) {
      const message = error.response?.data?.message || error.response?.data?.error || 'Erro ao duplicar estrutura de saída.';
      toast.error(message, { id: toastId });
      console.error('Erro ao duplicar estrutura de saída:', error);
    }
  };

  const handleSave = async (formData) => {
    try {
      setIsSubmitting(true);
      const token = localStorage.getItem('token');
      
      // Preparar payload com campos adicionais necessários
      let payload = { ...formData };
      
      // Adicionar campo type: "ai" se não estiver presente
      if (!payload.type) {
        payload.type = 'ai';
      }
      
      // Parse do schema se for string
      if (typeof payload.schema === 'string') {
        try {
          payload.schema = JSON.parse(payload.schema);
        } catch (e) {
          console.error('Erro ao fazer parse do schema:', e);
          throw new Error('Schema inválido. Verifique o formato JSON.');
        }
      }
      
      // Verificar se é edição (tem _id ou id e não é template)
      const isEditing = editingStructuredOutput && 
        (editingStructuredOutput._id || editingStructuredOutput.id) && 
        !editingStructuredOutput._isTemplate;
      
      if (isEditing) {
        // Atualizar
        await axios.put(
          `${API_BASE_URL}/api/structured-outputs/${editingStructuredOutput._id || editingStructuredOutput.id}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        toast.success('Estrutura de saída atualizada com sucesso');
      } else {
        // Criar (novo ou template)
        await axios.post(
          `${API_BASE_URL}/api/structured-outputs`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        toast.success('Estrutura de saída criada com sucesso');
      }

      setIsFormModalOpen(false);
      setEditingStructuredOutput(null);
      fetchStructuredOutputs();
    } catch (error) {
      console.error('Erro ao salvar estrutura de saída:', error);
      console.error('Resposta completa:', error.response?.data);
      
      // Extrair mensagem de erro mais detalhada
      let errorMessage = 'Erro ao salvar estrutura de saída';
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.error) {
          errorMessage = typeof error.response.data.error === 'string' 
            ? error.response.data.error 
            : JSON.stringify(error.response.data.error);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <DocumentTextIcon className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-bold">Estruturas de Saída</h1>
        </div>
        <Button onClick={handleCreate}>
          <PlusIcon className="h-5 w-5 mr-2" />
          Criar Estrutura de Saída
        </Button>
      </div>

      <Card className="p-4">
        <div className={`grid grid-cols-1 md:grid-cols-2 ${isAdmin ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-4`}>
          {/* Filtro por Nome */}
          <div>
            <Label htmlFor="nameFilter" className={labelStyle}>
              Filtrar por Nome
            </Label>
            <input
              id="nameFilter"
              type="text"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              className={inputStyle}
              placeholder="Digite o nome..."
            />
          </div>

          {/* Filtro por Data Início */}
          <div>
            <Label htmlFor="dataInicioFilter" className={labelStyle}>
              Data Início
            </Label>
            <input
              id="dataInicioFilter"
              type="date"
              value={dataInicioFilter}
              onChange={(e) => setDataInicioFilter(e.target.value)}
              className={inputStyle}
            />
          </div>

          {/* Filtro por Data Fim */}
          <div>
            <Label htmlFor="dataFimFilter" className={labelStyle}>
              Data Fim
            </Label>
            <input
              id="dataFimFilter"
              type="date"
              value={dataFimFilter}
              onChange={(e) => setDataFimFilter(e.target.value)}
              className={inputStyle}
            />
          </div>

          {/* Filtro por Cliente (apenas para admin) */}
          {isAdmin && (
            <div>
              <Label htmlFor="clienteIdFilter" className={labelStyle}>
                Filtrar por Cliente
              </Label>
              <select
                id="clienteIdFilter"
                value={clienteIdFilter}
                onChange={(e) => setClienteIdFilter(e.target.value)}
                className={selectStyle}
              >
                <option value="">Todos os clientes</option>
                {clientes.map((cliente) => (
                  <option key={cliente._id || cliente.id} value={cliente._id || cliente.id}>
                    {cliente.nome || cliente.name || cliente.email || cliente._id || cliente.id}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Botão para limpar filtros */}
        {(nameFilter || dataInicioFilter || dataFimFilter || (isAdmin && clienteIdFilter)) && (
          <div className="mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setNameFilter('');
                setDataInicioFilter('');
                setDataFimFilter('');
                setClienteIdFilter('');
              }}
            >
              Limpar Filtros
            </Button>
          </div>
        )}
      </Card>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">
          Carregando estruturas de saída...
        </div>
      ) : structuredOutputs.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Nenhuma estrutura de saída encontrada.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {structuredOutputs.map((so) => (
            <Card key={so._id || so.id}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-semibold">{so.name}</h3>
                      <Badge variant="secondary">{so.schema?.type || 'N/A'}</Badge>
                    </div>
                    {so.description && (
                      <p className="text-sm text-muted-foreground mb-3">{so.description}</p>
                    )}
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium">ID:</span>{' '}
                        <span className="text-muted-foreground font-mono text-xs">{so.vapiId || '-'}</span>
                      </div>
                      <div>
                        <span className="font-medium">Org ID:</span>{' '}
                        <span className="text-muted-foreground font-mono text-xs">{so.orgId || '-'}</span>
                      </div>
                      <div>
                        <span className="font-medium">Criado em:</span>{' '}
                        <span className="text-muted-foreground">{formatDate(so.createdAt)}</span>
                      </div>
                      <div>
                        <span className="font-medium">Atualizado em:</span>{' '}
                        <span className="text-muted-foreground">{formatDate(so.updatedAt)}</span>
                      </div>
                      {so.assistantIds && so.assistantIds.length > 0 && (
                        <div className="col-span-2">
                          <span className="font-medium">Assistentes:</span>{' '}
                          <span className="text-muted-foreground">{so.assistantIds.length} vinculado(s)</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(so)}
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDuplicate(so)}
                    >
                      <DocumentDuplicateIcon className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDelete(so._id || so.id)}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <TemplateSelectionModal
        isOpen={isTemplateModalOpen}
        onClose={() => setIsTemplateModalOpen(false)}
        onSelectTemplate={handleSelectTemplate}
        onCreateFromScratch={handleCreateFromScratch}
      />

      <Modal
        isOpen={isFormModalOpen}
        onClose={() => {
          setIsFormModalOpen(false);
          setEditingStructuredOutput(null);
        }}
      >
        <StructuredOutputForm
          structuredOutput={editingStructuredOutput}
          onSave={handleSave}
          onCancel={() => {
            setIsFormModalOpen(false);
            setEditingStructuredOutput(null);
          }}
          isSubmitting={isSubmitting}
        />
      </Modal>
    </div>
  );
};

export default StructuredOutputs;
